import { useEffect, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarIcon } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");

// Canonical display/entry format: dd-mmm-yy (e.g. 09-Apr-27)
export const fmtDate = (d) => `${pad(d.getDate())}-${MONTHS[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;

// Parse the formats the app may hold: dd-mmm-yy(yy), dd/mm/yyyy(yy), yyyy-mm-dd.
export const parseDate = (v) => {
  if (!v) return undefined;
  const s = String(v).trim();
  let m = /^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/.exec(s);
  if (m) {
    const mo = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase());
    if (mo >= 0) { let y = +m[3]; if (y < 100) y += 2000; return new Date(y, mo, +m[1]); }
  }
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(s);
  if (m) { let y = +m[3]; if (y < 100) y += 2000; return new Date(y, +m[2] - 1, +m[1]); }
  m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? undefined : d;
};

// Typeable date field (dd-mmm-yy) with a calendar popover.
export default function DatePicker({ value, onChange, placeholder = "dd-mmm-yy", className = "", testId }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  // Keep the visible text in sync with the stored value, normalised to dd-mmm-yy when it's a full date.
  useEffect(() => {
    const d = parseDate(value);
    setText(d ? fmtDate(d) : (value || ""));
  }, [value]);

  const commit = () => {
    const d = parseDate(text);
    if (d) onChange(fmtDate(d));
    else onChange(text); // preserve partial values (e.g. a day/month year-end)
  };

  return (
    <div className={`relative ${className}`}>
      <Input
        type="text"
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        data-testid={testId}
        className="w-full h-9 pr-9"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="icon"
            className="absolute right-0 top-0 h-9 w-9 text-cass-slate hover:text-cass-navy"
            data-testid={testId ? `${testId}-calendar` : undefined}>
            <CalendarIcon className="w-4 h-4 opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="single" selected={parseDate(value)} onSelect={(d) => { onChange(d ? fmtDate(d) : ""); setOpen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}
